import React from 'react';
import PropTypes from 'prop-types';
import { Card, CardBody, Button } from 'reactstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

/**
 * Error Boundary component to catch React errors and display fallback UI
 * @see https://react.dev/reference/react/Component#catching-rendering-errors-with-an-error-boundary
 */
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null
    };
  }

  static getDerivedStateFromError(error) {
    // Update state so the next render will show the fallback UI
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    // Log error details for debugging
    console.error('ErrorBoundary caught an error:', error, errorInfo);

    // You can also log to an error reporting service here
    // Example: Sentry.captureException(error, { extra: errorInfo });

    this.setState({
      error,
      errorInfo
    });
  }

  handleReset = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null
    });
  };

  render() {
    if (this.state.hasError) {
      // Render custom fallback UI
      return (
        <div className="container mt-5">
          <Card className="text-center">
            <CardBody className="p-5">
              <div className="mb-4">
                <FontAwesomeIcon icon="exclamation-triangle" size="4x" className="text-warning" />
              </div>
              <h2 className="mb-3">Oops! Something went wrong</h2>
              <p className="text-muted mb-4">
                {this.props.showDetails && this.state.error
                  ? this.state.error.toString()
                  : 'An unexpected error occurred. Please try again.'}
              </p>

              <div className="d-flex gap-2 justify-content-center">
                <Button color="falcon-primary" onClick={this.handleReset}>
                  <FontAwesomeIcon icon="redo" className="me-2" />
                  Try Again
                </Button>
                <Button color="falcon-default" onClick={() => (window.location.href = '/')}>
                  <FontAwesomeIcon icon="home" className="me-2" />
                  Go to Dashboard
                </Button>
              </div>

              {this.props.showDetails && this.state.errorInfo && (
                <details className="mt-4 text-left">
                  <summary className="cursor-pointer text-primary">Show Error Details</summary>
                  <pre className="mt-3 p-3 bg-light rounded text-small">
                    {this.state.error && this.state.error.toString()}
                    <br />
                    {this.state.errorInfo.componentStack}
                  </pre>
                </details>
              )}
            </CardBody>
          </Card>
        </div>
      );
    }

    return this.props.children;
  }
}

ErrorBoundary.propTypes = {
  children: PropTypes.node.isRequired,
  showDetails: PropTypes.bool
};

ErrorBoundary.defaultProps = {
  showDetails: process.env.NODE_ENV === 'development'
};

export default ErrorBoundary;
